import React, { useEffect, useMemo, useState } from "react";
import { listAll, createDoc, updateDocById, removeDoc } from "../../services/firestore";
import { uploadImageToR2 } from "../../services/r2";
import { autoTranslatePatch } from "../../services/translation";
import { Plus, Trash2, Edit3, X, Upload, Image as ImageIcon, Search, Filter, AlertTriangle } from "lucide-react";

const LOW_STOCK_THRESHOLD = 5;

const STOCK_FILTERS = [
  ["all", "Hamısı"],
  ["in_stock", "Stokda var"],
  ["low", `Az stok (≤${LOW_STOCK_THRESHOLD})`],
  ["out", "Bitmiş"],
];

const EXTRA_FILTERS = [
  ["featured", "Ön planda"],
  ["limited", "Limitli"],
  ["new_arrival", "Yeni gələn"],
  ["is_used", "İkinci əl"],
];

const EMPTY = {
  title: "", slug: "", artist_id: "", category_id: "", category_ids: [],
  price_azn: 0, cover_url: "", description: "",
  catalog_no: "", format: "180G VINYL LP", rpm: "33 ⅓ RPM", weight: "180g",
  release_date: "", tracklist: [], spotify_track_id: "", spotify_album_id: "", stock: 0,
  featured: false, limited: false, new_arrival: false,
  is_used: false, condition: "",
};

function slugify(s) {
  return (s || "").toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

// Spotify linkindən ID və növ çıxar. URL, URI və ya çılpaq ID dəstəklənir.
// Geriyə {type:"track"|"album"|"playlist", id} qaytarır; tapılmasa null.
function parseSpotifyLink(input) {
  if (!input) return null;
  const s = String(input).trim();
  if (!s) return null;
  const urlMatch = s.match(/open\.spotify\.com\/(?:embed\/)?(track|album|playlist)\/([a-zA-Z0-9]+)/i);
  if (urlMatch) return { type: urlMatch[1].toLowerCase(), id: urlMatch[2] };
  const uriMatch = s.match(/spotify:(track|album|playlist):([a-zA-Z0-9]+)/i);
  if (uriMatch) return { type: uriMatch[1].toLowerCase(), id: uriMatch[2] };
  // çılpaq ID — defaultca track kimi qəbul et
  if (/^[a-zA-Z0-9]{8,}$/.test(s)) return { type: "track", id: s };
  return null;
}

// Köhnə uyğunluq üçün — yalnız track ID-ni qaytarır.
function extractSpotifyId(input) {
  const parsed = parseSpotifyLink(input);
  return parsed && parsed.type === "track" ? parsed.id : "";
}

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [artists, setArtists] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");

  // ── Listing filters ─────────────────────────────────────────────
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [flagFilter, setFlagFilter] = useState("");        // featured|limited|new_arrival|is_used | ""
  const [sortKey, setSortKey] = useState("title");          // title|price_asc|price_desc|stock_asc|stock_desc

  const reload = async () => {
    const [p, a, c] = await Promise.all([listAll("products"), listAll("artists"), listAll("categories")]);
    setItems(p); setArtists(a); setCats(c);
  };
  useEffect(() => { reload(); }, []);

  // Build sorted-with-depth category list so the picker shows the full hierarchy.
  const sortedCats = React.useMemo(() => {
    const byParent = new Map();
    for (const c of cats) {
      const k = c.parent_id || "_root";
      if (!byParent.has(k)) byParent.set(k, []);
      byParent.get(k).push(c);
    }
    for (const [, arr] of byParent) arr.sort((a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name));
    const out = [];
    const walk = (parent, depth) => {
      const list = byParent.get(parent || "_root") || [];
      for (const node of list) {
        out.push({ ...node, _depth: depth });
        walk(node.id, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  }, [cats]);

  const open = (it) => {
    if (it) {
      // Backward compat: derive category_ids from legacy single category_id when missing
      const ids = Array.isArray(it.category_ids) && it.category_ids.length
        ? it.category_ids
        : (it.category_id ? [it.category_id] : []);
      setEditing({ ...it, category_ids: ids });
    } else {
      setEditing({ ...EMPTY });
    }
    setUploadErr("");
  };
  const close = () => setEditing(null);

  const onTitleChange = (v) => setEditing((s) => ({ ...s, title: v, slug: s.slug || slugify(v) }));

  const toggleCategory = (id) => {
    setEditing((s) => {
      const cur = Array.isArray(s.category_ids) ? s.category_ids : [];
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      return { ...s, category_ids: next, category_id: next[0] || "" };
    });
  };

  const save = async () => {
    if (!editing.title) return alert("Məhsulun başlığını yazın.");
    setBusy(true);
    try {
      const cIds = Array.isArray(editing.category_ids) ? editing.category_ids.filter(Boolean) : [];
      // Auto-translate text fields (AZ → EN, RU). Silent failure falls back to source.
      const i18n = await autoTranslatePatch({
        title: editing.title || "",
        description: editing.description || "",
        condition: editing.is_used ? (editing.condition || "") : "",
      });

      // Spotify: hər iki sahə (track/album linki) müstəqil parse edilir.
      // Boş və ya yanlış linklər boş string kimi yazılır.
      const trackParsed = parseSpotifyLink(editing.spotify_track_id);
      const albumParsed = parseSpotifyLink(editing.spotify_album_id);
      // Əgər istifadəçi yanlışlıqla bir sahəyə əks tipli link yapışdırıbsa,
      // avtomatik olaraq düzgün sahəyə yönləndir.
      let spotify_track_id = "";
      let spotify_album_id = "";
      if (trackParsed?.type === "track") spotify_track_id = trackParsed.id;
      else if (trackParsed?.type === "album") spotify_album_id = trackParsed.id;
      if (albumParsed?.type === "album") spotify_album_id = albumParsed.id;
      else if (albumParsed?.type === "track" && !spotify_track_id) spotify_track_id = albumParsed.id;

      // YENİ badge timestamp: stamp the moment the admin flips "Yeni gələn"
      // ON. The homepage hides the badge automatically 14 days later.
      // - Creating a new product with the checkbox on → set timestamp.
      // - Editing an existing product:
      //     • was OFF, now ON  → set timestamp to now
      //     • stays ON         → preserve previous timestamp
      //     • turned OFF       → clear timestamp
      const wasNew = !!items.find((p) => p.id === editing.id)?.new_arrival;
      const previousSetAt = items.find((p) => p.id === editing.id)?.new_arrival_set_at;
      let new_arrival_set_at;
      if (editing.new_arrival) {
        if (!editing.id) new_arrival_set_at = new Date().toISOString();
        else if (!wasNew) new_arrival_set_at = new Date().toISOString();
        else new_arrival_set_at = previousSetAt ?? new Date().toISOString();
      } else {
        new_arrival_set_at = null;
      }

      const data = {
        ...editing,
        ...i18n,
        slug: editing.slug || slugify(editing.title),
        price_azn: parseFloat(editing.price_azn) || 0,
        stock: parseInt(editing.stock, 10) || 0,
        spotify_track_id,
        spotify_album_id,
        category_ids: cIds,
        category_id: cIds[0] || editing.category_id || "",
        is_used: !!editing.is_used,
        condition: editing.is_used ? (editing.condition || "") : "",
        new_arrival: !!editing.new_arrival,
        new_arrival_set_at,
      };
      if (editing.id) await updateDocById("products", editing.id, data);
      else await createDoc("products", data);
      await reload(); close();
    } finally { setBusy(false); }
  };

  const del = async (it) => {
    if (!window.confirm(`'${it.title}' silinsin?`)) return;
    await removeDoc("products", it.id); reload();
  };

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setUploadErr("");
    try {
      const url = await uploadImageToR2(f, { folder: "products" });
      setEditing((s) => ({ ...s, cover_url: url }));
    } catch (ex) {
      setUploadErr(ex.message || "Yükləmə alınmadı.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // ── Counters for filter chips (computed from the full unfiltered list)
  const counts = useMemo(() => {
    const c = { all: items.length, in_stock: 0, low: 0, out: 0,
                featured: 0, limited: 0, new_arrival: 0, is_used: 0 };
    for (const it of items) {
      const s = Number(it.stock || 0);
      if (s === 0) c.out += 1;
      else { c.in_stock += 1; if (s <= LOW_STOCK_THRESHOLD) c.low += 1; }
      if (it.featured) c.featured += 1;
      if (it.limited) c.limited += 1;
      if (it.new_arrival) c.new_arrival += 1;
      if (it.is_used) c.is_used += 1;
    }
    return c;
  }, [items]);

  // ── Apply filters + sorting to produce the rendered list
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    let arr = items.filter((it) => {
      if (term) {
        const hay = [it.title, it.slug, it.catalog_no, it.format].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (catFilter !== "all") {
        const ids = Array.isArray(it.category_ids) && it.category_ids.length
          ? it.category_ids : (it.category_id ? [it.category_id] : []);
        if (!ids.includes(catFilter)) return false;
      }
      const s = Number(it.stock || 0);
      if (stockFilter === "in_stock" && s <= 0) return false;
      if (stockFilter === "low" && (s <= 0 || s > LOW_STOCK_THRESHOLD)) return false;
      if (stockFilter === "out" && s !== 0) return false;
      if (flagFilter && !it[flagFilter]) return false;
      return true;
    });
    const cmp = {
      title:       (a, b) => (a.title || "").localeCompare(b.title || ""),
      price_asc:   (a, b) => Number(a.price_azn || 0) - Number(b.price_azn || 0),
      price_desc:  (a, b) => Number(b.price_azn || 0) - Number(a.price_azn || 0),
      stock_asc:   (a, b) => Number(a.stock || 0) - Number(b.stock || 0),
      stock_desc:  (a, b) => Number(b.stock || 0) - Number(a.stock || 0),
    }[sortKey] || (() => 0);
    return arr.sort(cmp);
  }, [items, query, catFilter, stockFilter, flagFilter, sortKey]);

  const hasActiveFilter = query.trim() || catFilter !== "all" || stockFilter !== "all" || flagFilter;
  const clearFilters = () => { setQuery(""); setCatFilter("all"); setStockFilter("all"); setFlagFilter(""); };

  return (
    <div className="p-4 sm:p-6 md:p-10" data-testid="admin-products">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 sm:mb-8">
        <div className="min-w-0">
          <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Kataloq</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl mt-1">Məhsullar</h1>
          <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] mt-2 flex flex-wrap gap-x-3 gap-y-1">
            <span>{visible.length} / {items.length} məhsul</span>
            {counts.low > 0 && (
              <span className="inline-flex items-center gap-1 text-[var(--accent)]">
                <AlertTriangle size={11} /> {counts.low} az stok
              </span>
            )}
            {counts.out > 0 && (
              <span className="inline-flex items-center gap-1 text-[var(--accent)]">
                <AlertTriangle size={11} /> {counts.out} bitmiş
              </span>
            )}
          </div>
        </div>
        <button onClick={() => open(null)} className="btn-primary w-full sm:w-auto justify-center" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="add-product">
          <Plus size={14} /> Məhsul əlavə et
        </button>
      </div>

      {/* ── Filter toolbar ──────────────────────────────────────────── */}
      <div className="border border-[var(--border)] bg-[var(--surface)]/30 p-3 sm:p-4 mb-4 sm:mb-6 space-y-3">
        {/* Search + category + sort */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2 border border-[var(--border)] px-3 py-2 sm:py-1.5 flex-1 min-w-0 sm:min-w-[260px] bg-[var(--bg)]">
            <Search size={14} className="text-[var(--text-muted)] flex-shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Başlıq, slug, katalog № axtar…"
              className="flex-1 min-w-0 bg-transparent outline-none text-sm"
              data-testid="prod-search"
            />
            {query && <button onClick={() => setQuery("")} className="text-[var(--text-muted)] flex-shrink-0"><X size={14} /></button>}
          </div>
          <div className="flex gap-2 sm:gap-3">
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              className="bg-transparent border border-[var(--border)] px-2 sm:px-3 py-2 sm:py-1.5 text-sm flex-1 sm:flex-none sm:min-w-[200px] max-w-full"
              data-testid="prod-cat-filter"
            >
              <option value="all">Bütün kateqoriyalar</option>
              {sortedCats.map((c) => (
                <option key={c.id} value={c.id}>
                  {"— ".repeat(c._depth)}{c.name}
                </option>
              ))}
            </select>
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              className="bg-transparent border border-[var(--border)] px-2 sm:px-3 py-2 sm:py-1.5 text-sm flex-1 sm:flex-none"
              data-testid="prod-sort"
            >
              <option value="title">A → Z</option>
              <option value="price_asc">Qiymət ↑</option>
              <option value="price_desc">Qiymət ↓</option>
              <option value="stock_asc">Stok ↑ (azaldan)</option>
              <option value="stock_desc">Stok ↓ (çoxaldan)</option>
            </select>
          </div>
        </div>

        {/* Stock chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium flex items-center gap-1 mr-2">
            <Filter size={12} /> Stok:
          </span>
          {STOCK_FILTERS.map(([k, label]) => (
            <button
              key={k}
              onClick={() => setStockFilter(k)}
              className={`px-3 py-1.5 text-xs border ${
                stockFilter === k
                  ? "bg-[var(--primary)] text-[var(--inverse)] border-[var(--primary)]"
                  : "border-[var(--border)] hover:border-[var(--primary)]"
              }`}
              style={{ textTransform: "none", letterSpacing: "0" }}
              data-testid={`prod-stock-${k}`}
            >
              {label} <span className="opacity-60">({counts[k] ?? 0})</span>
            </button>
          ))}
        </div>

        {/* Extra flag filters */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium mr-2">Xüsusiyyət:</span>
          <button
            onClick={() => setFlagFilter("")}
            className={`px-3 py-1.5 text-xs border ${!flagFilter ? "bg-[var(--primary)] text-[var(--inverse)] border-[var(--primary)]" : "border-[var(--border)] hover:border-[var(--primary)]"}`}
            style={{ textTransform: "none", letterSpacing: "0" }}
          >Hamısı</button>
          {EXTRA_FILTERS.map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFlagFilter(flagFilter === k ? "" : k)}
              className={`px-3 py-1.5 text-xs border ${
                flagFilter === k
                  ? "bg-[var(--primary)] text-[var(--inverse)] border-[var(--primary)]"
                  : "border-[var(--border)] hover:border-[var(--primary)]"
              }`}
              style={{ textTransform: "none", letterSpacing: "0" }}
              data-testid={`prod-flag-${k}`}
            >
              {label} <span className="opacity-60">({counts[k] ?? 0})</span>
            </button>
          ))}
          {hasActiveFilter && (
            <button onClick={clearFilters} className="ml-auto text-xs px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="prod-clear-filters">
              Filtrləri təmizlə
            </button>
          )}
        </div>
      </div>

      {/* ── Cədvəl ──────────────────────────────────────────────────── */}
      <div className="border border-[var(--border)]">
        {/* Desktop header - hidden on mobile */}
        <div className="hidden md:grid grid-cols-12 text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium px-4 py-3 border-b border-[var(--border)] bg-[var(--surface)]">
          <div className="col-span-1">Şəkil</div>
          <div className="col-span-4">Başlıq</div>
          <div className="col-span-3">Kateqoriya</div>
          <div className="col-span-1">Stok</div>
          <div className="col-span-1">Qiymət</div>
          <div className="col-span-2 text-right">Əməl</div>
        </div>
        {visible.map((it) => {
          const stockN = Number(it.stock || 0);
          const stockCls = stockN === 0
            ? "text-[var(--accent)]"
            : stockN <= LOW_STOCK_THRESHOLD
              ? "text-amber-500"
              : "text-[var(--text-2)]";
          const catIds = Array.isArray(it.category_ids) && it.category_ids.length ? it.category_ids : (it.category_id ? [it.category_id] : []);
          const catNames = catIds.map((id) => cats.find((c) => c.id === id)?.name).filter(Boolean);
          return (
          <div key={it.id} className="border-b border-[var(--border-soft)]" data-testid={`prod-row-${it.id}`}>
            {/* Desktop row */}
            <div className="hidden md:grid grid-cols-12 px-4 py-3 items-center text-sm">
              <div className="col-span-1">
                {it.cover_url ? <img src={it.cover_url} alt="" className="w-12 h-12 object-cover" /> : <div className="w-12 h-12 bg-[var(--surface)] flex items-center justify-center"><ImageIcon size={18} className="text-[var(--text-muted)]" /></div>}
              </div>
              <div className="col-span-4 font-display text-lg flex items-center gap-2">
                <span>{it.title}</span>
                {it.is_used && <span className="text-[10px] px-1.5 py-0.5 border border-[var(--border)] text-[var(--text-muted)]" style={{ letterSpacing: "0.04em" }}>İkinci əl</span>}
              </div>
              <div className="col-span-3 text-[var(--text-2)] truncate text-sm">
                {catNames.length ? catNames.join(", ") : "—"}
              </div>
              <div className={`col-span-1 font-mono text-sm ${stockCls}`}>
                {stockN}
                {stockN === 0 && <div className="text-[10px] text-[var(--accent)]">Bitib</div>}
                {stockN > 0 && stockN <= LOW_STOCK_THRESHOLD && <div className="text-[10px] text-amber-500">Az</div>}
              </div>
              <div className="col-span-1 font-mono">₼{Number(it.price_azn).toFixed(2)}</div>
              <div className="col-span-2 flex justify-end gap-2">
                <button onClick={() => open(it)} className="p-1 hover:text-[var(--primary)]" data-testid={`edit-${it.id}`}><Edit3 size={14} /></button>
                <button onClick={() => del(it)} className="p-1 hover:text-[var(--accent)]" data-testid={`del-${it.id}`}><Trash2 size={14} /></button>
              </div>
            </div>

            {/* Mobile card row */}
            <div className="md:hidden p-3 flex gap-3">
              {it.cover_url ? (
                <img src={it.cover_url} alt="" className="w-16 h-16 object-cover flex-shrink-0" />
              ) : (
                <div className="w-16 h-16 bg-[var(--surface)] flex items-center justify-center flex-shrink-0"><ImageIcon size={18} className="text-[var(--text-muted)]" /></div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-base leading-tight truncate">{it.title}</div>
                    <div className="text-[11px] text-[var(--text-muted)] truncate mt-0.5">
                      {catNames.length ? catNames.join(", ") : "—"}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => open(it)} className="p-2 border border-[var(--border)] hover:border-[var(--primary)] hover:text-[var(--primary)]" data-testid={`edit-m-${it.id}`}><Edit3 size={14} /></button>
                    <button onClick={() => del(it)} className="p-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]" data-testid={`del-m-${it.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                  <div className="font-mono text-sm">₼{Number(it.price_azn).toFixed(2)}</div>
                  <div className={`font-mono text-xs ${stockCls} flex items-center gap-1`}>
                    Stok: {stockN}
                    {stockN === 0 && <span className="text-[10px] px-1.5 py-0.5 border border-[var(--accent)]/40 text-[var(--accent)]">Bitib</span>}
                    {stockN > 0 && stockN <= LOW_STOCK_THRESHOLD && <span className="text-[10px] px-1.5 py-0.5 border border-amber-500/40 text-amber-500">Az</span>}
                  </div>
                  {it.is_used && <span className="text-[10px] px-1.5 py-0.5 border border-[var(--border)] text-[var(--text-muted)]" style={{ letterSpacing: "0.04em" }}>İkinci əl</span>}
                </div>
              </div>
            </div>
          </div>
          );
        })}
        {!visible.length && (
          <div className="p-10 text-center text-[var(--text-muted)]">
            {items.length === 0 ? "Hələ məhsul yoxdur" : "Seçilən filtrə uyğun məhsul yoxdur"}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-2 sm:p-4 md:p-6">
          <div className="bg-[var(--bg)] border border-[var(--border)] w-full max-w-3xl max-h-[95vh] md:max-h-[90vh] flex flex-col" data-testid="product-modal">
            <div className="flex justify-between items-center px-4 sm:px-6 md:px-8 py-4 border-b border-[var(--border)] bg-[var(--bg)] sticky top-0 z-10">
              <h2 className="font-display text-xl sm:text-2xl md:text-3xl">{editing.id ? "Düzəliş" : "Yeni"} Məhsul</h2>
              <button onClick={close} data-testid="product-modal-close" aria-label="Bağla" className="p-1 hover:text-[var(--accent)]"><X /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 py-4 md:py-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Başlıq <span className="text-[var(--text-muted)] font-normal normal-case">— məhsulun adı</span></label>
                <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.title} onChange={(e) => onTitleChange(e.target.value)} data-testid="f-title" placeholder="Məsələn: Midnight in Baku" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Kataloq nömrəsi <span className="text-[var(--text-muted)] font-normal normal-case">— daxili kod (istəyə bağlı)</span></label>
                <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono" value={editing.catalog_no} onChange={(e) => setEditing({ ...editing, catalog_no: e.target.value })} placeholder="BAKU-001-V" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Sənətçi <span className="text-[var(--text-muted)] font-normal normal-case">— istəyə bağlı</span></label>
                <select className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.artist_id} onChange={(e) => setEditing({ ...editing, artist_id: e.target.value })} data-testid="f-artist">
                  <option value="">— yoxdur (musiqiyə aid deyil) —</option>
                  {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Kateqoriyalar <span className="text-[var(--text-muted)] font-normal normal-case">— bir və ya daha çox seçə bilərsiniz (alt-kateqoriyalar daxil)</span></label>
                <div className="mt-1 border border-[var(--border)] max-h-56 overflow-y-auto p-2 space-y-1" data-testid="f-categories">
                  {sortedCats.length === 0 && (
                    <div className="text-[var(--text-muted)] text-sm p-2">Hələ kateqoriya yoxdur</div>
                  )}
                  {sortedCats.map((c) => {
                    const checked = (editing.category_ids || []).includes(c.id);
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-2 cursor-pointer hover:bg-[var(--surface)] px-2 py-1 ${checked ? "text-[var(--primary)]" : ""}`}
                        style={{ paddingLeft: 8 + c._depth * 18 }}
                        data-testid={`f-cat-${c.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCategory(c.id)}
                        />
                        {c._depth > 0 && <span className="text-[var(--text-muted)] text-xs">↳</span>}
                        <span className="text-sm">{c.name}</span>
                      </label>
                    );
                  })}
                </div>
                {(editing.category_ids || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(editing.category_ids || []).map((id) => {
                      const c = cats.find((x) => x.id === id);
                      if (!c) return null;
                      return (
                        <span key={id} className="text-xs px-2 py-1 border border-[var(--border)] flex items-center gap-2">
                          {c.name}
                          <button type="button" onClick={() => toggleCategory(id)} className="hover:text-[var(--accent)]">×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Qiymət (AZN)</label>
                <input type="number" step="0.01" className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.price_azn} onChange={(e) => setEditing({ ...editing, price_azn: e.target.value })} data-testid="f-price" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Stok <span className="text-[var(--text-muted)] font-normal normal-case">— neçə ədəd var</span></label>
                <input type="number" className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Format <span className="text-[var(--text-muted)] font-normal normal-case">— LP / EP / CD</span></label>
                <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.format} onChange={(e) => setEditing({ ...editing, format: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">RPM <span className="text-[var(--text-muted)] font-normal normal-case">— vinil fırlanma sürəti</span></label>
                <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.rpm} onChange={(e) => setEditing({ ...editing, rpm: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Çəki</label>
                <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.weight} onChange={(e) => setEditing({ ...editing, weight: e.target.value })} placeholder="180g" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Buraxılış tarixi</label>
                <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.release_date} onChange={(e) => setEditing({ ...editing, release_date: e.target.value })} placeholder="2024" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Açıqlama <span className="text-[var(--text-muted)] font-normal normal-case">— məhsul haqqında mətn</span></label>
                <textarea rows={3} className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="md:col-span-2 border border-[var(--border-soft)] p-4 bg-[var(--surface)]/30">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editing.is_used}
                    onChange={(e) => setEditing({ ...editing, is_used: e.target.checked })}
                    data-testid="f-is-used"
                  />
                  <span className="font-medium">İkinci əl məhsuldur</span>
                  <span className="text-[var(--text-muted)] font-normal text-xs">— işlənmiş / ikinci əl olarsa qeyd edin</span>
                </label>
                {editing.is_used && (
                  <div className="mt-3" data-testid="f-condition-wrap">
                    <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Vəziyyət <span className="text-[var(--text-muted)] font-normal normal-case">— məhsulun cari vəziyyətini ətraflı yazın (cızıq, səs keyfiyyəti, qab və s.)</span></label>
                    <textarea
                      rows={3}
                      className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                      placeholder="Məsələn: Vinil A tərəfində yüngül cızıqlar var, səs keyfiyyəti yüksək, orijinal qabıq qorunub..."
                      value={editing.condition || ""}
                      onChange={(e) => setEditing({ ...editing, condition: e.target.value })}
                      data-testid="f-condition"
                    />
                  </div>
                )}
              </div>
              <div className="md:col-span-2 grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Spotify trek linki <span className="text-[var(--text-muted)] font-normal normal-case">— istəyə bağlı</span></label>
                  <input
                    className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-sm"
                    placeholder="https://open.spotify.com/track/..."
                    value={editing.spotify_track_id || ""}
                    onChange={(e) => setEditing({ ...editing, spotify_track_id: e.target.value })}
                    data-testid="f-spotify-track"
                  />
                  <div className="text-xs text-[var(--text-muted)] mt-1">Tək mahnı linki / URI / ID</div>
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Spotify albom linki <span className="text-[var(--text-muted)] font-normal normal-case">— istəyə bağlı</span></label>
                  <input
                    className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-sm"
                    placeholder="https://open.spotify.com/album/..."
                    value={editing.spotify_album_id || ""}
                    onChange={(e) => setEditing({ ...editing, spotify_album_id: e.target.value })}
                    data-testid="f-spotify-album"
                  />
                  <div className="text-xs text-[var(--text-muted)] mt-1">Albom linki yapışdırsanız bütün treklər saytda görünəcək. Hər ikisi doldurulsa, albom prioritetdir.</div>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Üz şəkli</label>
                <div className="mt-1 flex items-center gap-3">
                  {editing.cover_url ? <img src={editing.cover_url} alt="" className="w-20 h-20 object-cover border border-[var(--border)]" /> : <div className="w-20 h-20 bg-[var(--surface)] flex items-center justify-center"><ImageIcon size={20} className="text-[var(--text-muted)]" /></div>}
                  <label className="btn-outline cursor-pointer" style={{ textTransform: "none", letterSpacing: "0" }}>
                    <Upload size={14} /> {uploading ? "Yüklənir…" : "R2-yə yüklə"}
                    <input type="file" accept="image/*" hidden onChange={onPickImage} data-testid="f-image-upload" />
                  </label>
                  <input className="flex-1 bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs" placeholder="və ya şəkil URL-i yapışdırın" value={editing.cover_url} onChange={(e) => setEditing({ ...editing, cover_url: e.target.value })} data-testid="f-cover-url" />
                </div>
                {uploadErr && <div className="text-[var(--accent)] text-sm mt-2">{uploadErr}</div>}
              </div>
              <div className="md:col-span-2 flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Ön planda göstər</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.limited} onChange={(e) => setEditing({ ...editing, limited: e.target.checked })} /> Limitli tiraj</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.new_arrival} onChange={(e) => setEditing({ ...editing, new_arrival: e.target.checked })} /> Yeni gələn</label>
              </div>
            </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 px-4 sm:px-6 md:px-8 py-4 border-t border-[var(--border)] bg-[var(--bg)] sticky bottom-0 z-10">
              <button onClick={close} className="btn-outline w-full sm:w-auto" style={{ textTransform: "none", letterSpacing: "0" }}>Ləğv et</button>
              <button onClick={save} disabled={busy} className="btn-primary w-full sm:w-auto" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="f-save">{busy ? "…" : "Yadda saxla"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
